import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal, Upload, message } from 'antd';
import { 
    FileTextOutlined, 
    CloseOutlined,
    InboxOutlined
} from '@ant-design/icons';
import { Flex, FlexColumn } from '../../Reusable/Container';
import LucideIcon, { Button, TextButton } from '../../Reusable/Button';
import { ErrorAlert } from '../../Reusable/Alert';
import { apiPOSTReq } from '../../../Utils/api';
import environment from '../../../environment';
import { LucideFileText, LucideFileUp, LucideTrash2, LucideX } from 'lucide-react';
import { useTheme, withTheme } from 'styled-components';
import { Text } from '../../Reusable/Text';
import { connect } from 'react-redux';
import hexToRgba from 'hex-to-rgba';
import UploadIcon from '../../../Images/upload-cloud.svg'
import { Image } from '../../Reusable/Image';

const { Dragger } = Upload;

class BillUpload extends Component {
    state = {
        fileList: [],
        uploading: false,
        processingBills: false,
        globalDropOccurred: false
    };
    
    componentDidMount() {
        // Add global event listeners if enableGlobalDrop is true
        if (this.props.enableGlobalDrop) {
            this.addGlobalEventListeners();
        }
    }
    
    componentDidUpdate(prevProps) {
        // Add or remove global event listeners when enableGlobalDrop changes
        if (this.props.enableGlobalDrop !== prevProps.enableGlobalDrop) {
            if (this.props.enableGlobalDrop) {
                this.addGlobalEventListeners();
            } else {
                this.removeGlobalEventListeners();
            }
        }
        
        // Reset fileList when modal becomes visible and no global drop just occurred
        if (this.props.visible && !prevProps.visible && !this.state.globalDropOccurred) {
            this.setState({ fileList: [] });
        }
        
        // Reset the globalDropOccurred flag after modal is displayed
        if (this.props.visible && this.state.globalDropOccurred) {
            this.setState({ globalDropOccurred: false });
        }
    }
    
    componentWillUnmount() {
        // Clean up event listeners
        this.removeGlobalEventListeners();
    }
    
    addGlobalEventListeners = () => {
        document.addEventListener('dragover', this.handleDragOver);
        document.addEventListener('drop', this.handleDocumentDrop);
    }
    
    removeGlobalEventListeners = () => {
        document.removeEventListener('dragover', this.handleDragOver);
        document.removeEventListener('drop', this.handleDocumentDrop);
    }
    
    // Prevent browser's default dragover behavior
    handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }
    
    // Handle file drop anywhere on the document
    handleDocumentDrop = (e) => {
        // Prevent browser from opening the file
        e.preventDefault();
        e.stopPropagation();
        
        // Only process if global drop is enabled
        if (!this.props.enableGlobalDrop) return;
        
        // Process the dropped files
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            const validFiles = files.filter(file => file.type === 'application/pdf');
            
            if (validFiles.length === 0) {
                message.error('Please drop only PDF files');
                return;
            }
            
            // Track that a global drop occurred
            this.setState({ globalDropOccurred: true });
            
            // Add valid files to our state (avoiding duplicates)
            this.setState(state => {
                // Get current filenames to check for duplicates
                const existingFilenames = state.fileList.map(f => f.name);
                
                // Filter out duplicates
                const newFiles = validFiles.filter(file => !existingFilenames.includes(file.name))
                    .map(file => ({
                        uid: Date.now() + Math.random().toString(36).substring(2, 10), // Ensure unique ID
                        status: 'done',
                        name: file.name,
                        originFileObj: file // Store the actual file object
                    }));
                
                if (validFiles.length > newFiles.length) {
                    message.info(`${validFiles.length - newFiles.length} duplicate ${validFiles.length - newFiles.length === 1 ? 'file was' : 'files were'} skipped`);
                }
                
                return {
                    fileList: [...state.fileList, ...newFiles]
                };
            }, () => {
                // If not already visible, show the upload modal
                if (!this.props.visible) {
                    this.props.onShowModal();
                }
            });
            
            // If any files were invalid, show a message
            if (validFiles.length < files.length) {
                message.warning(`${files.length - validFiles.length} non-PDF ${files.length - validFiles.length === 1 ? 'file was' : 'files were'} ignored`);
            }
        }
    }

    // Process uploaded bills once we have S3 URLs
    processUploadedBills = (fileUrls) => {
        this.setState({ processingBills: true });

        var { UserInfo } = this.props.store;
        UserInfo = UserInfo || {};
        var username = UserInfo.Email;
        var name = `${UserInfo.FirstName} ${UserInfo.LastName}`;
        
        // Use the improved API that can handle multiple attachments in one call
        apiPOSTReq(
            `${environment.payBaseUrl}/payables/bills/parseBillsManual`,
            {},
            { 
                "billFrom": name,
                "billSender": username,
                "attachmentUrls": fileUrls
            }, // Use the new field for multiple URLs
            (err, resp) => {
                this.setState({ processingBills: false, uploading: false });
                
                if (err) {
                    message.error('Failed to process bills');
                    console.error("Error processing bills:", err);
                    return;
                }
                
                try {
                    const data = resp;
                    if (data.result) {
                        // Determine how many bills were processed
                        const processedCount = data.processedCount || fileUrls.length;
                        const failedCount = data.failedCount || 0;
                        
                        // Show success message
                        message.success(`Successfully processed ${processedCount} ${processedCount === 1 ? 'bill' : 'bills'}`);
                        
                        // If any failed, show a warning
                        if (failedCount > 0) {
                            message.warning(`${failedCount} ${failedCount === 1 ? 'bill' : 'bills'} could not be processed`);
                        }
                        
                        // Close modal and notify parent of success
                        this.props.onUploadSuccess();
                    } else {
                        throw Error(data.error || data.responseMessage || "Failed to process bills");
                    }
                } catch (error) {
                    console.error("Error processing bills response:", error);
                    ErrorAlert({ description: error.message });
                }
            }
        );
    };

    // Main upload handler
    handleUpload = () => {
        const { fileList } = this.state;
        const { aionStore } = this.props;
        
        if (fileList.length === 0) {
            message.warning('Please select at least one file to upload');
            return;
        }

        this.setState({ uploading: true });
        
        // Upload each file to get S3 URLs
        const uploadPromises = fileList.map(file => {
            return new Promise((resolve, reject) => {
                // Get the actual file object to upload
                const fileToUpload = file.originFileObj || file;
                
                // Create a form data object
                const formData = new FormData();
                formData.append('file', fileToUpload);
                
                // Make a POST request to upload the file
                fetch(`${environment.apiBaseUrl}/file/upload`, {
                    method: 'POST',
                    headers: {
                        businesskey: aionStore.BusinessUniqueKey,
                        jwt: aionStore.jwt
                    },
                    body: formData,
                })
                .then(response => response.json())
                .then(data => {
                    if (data.UploadedUrls && data.UploadedUrls.length > 0) {
                        resolve(data.UploadedUrls[0]);
                    } else {
                        reject(new Error('Failed to get uploaded URL'));
                    }
                })
                .catch(error => {
                    reject(error);
                });
            });
        });

        Promise.allSettled(uploadPromises)
            .then(results => {
                const fileUrls = results
                    .filter(r => r.status === 'fulfilled')
                    .map(r => r.value);
                
                if (fileUrls.length > 0) {
                    this.processUploadedBills(fileUrls);
                } else {
                    message.error('Failed to upload files');
                    this.setState({ uploading: false });
                }
            })
            .catch(error => {
                message.error(`Upload failed: ${error.message}`);
                this.setState({ uploading: false });
            });
    };
    
    handleFileRemove = (file) => {
        this.setState(state => ({
            fileList: state.fileList.filter(f => f.uid !== file.uid)
        }));
    };
    
    // Clear file list and call parent's onCancel
    handleCancel = () => {
        this.setState({ 
            fileList: [],
            globalDropOccurred: false
        });
        this.props.onCancel();
    };
    
    // Modified Dragger handler to better sync with global drop handler
    handleDraggerChange = (info) => {
        // This gets called when files are added through the Dragger
        if (info.file && info.file.status === 'done') {
            console.log("File selected:", info.file);
            
            // Get the actual file object
            const newFile = info.file.originFileObj;
            
            if (!newFile) {
                console.error("Missing file object:", info.file);
                return;
            }
            
            // Check if it's a PDF
            const isPDF = newFile.type === 'application/pdf';
            if (!isPDF) {
                message.error(`${newFile.name} is not a PDF file`);
                return;
            }
            
            // Use the same duplicate checking logic as handleDocumentDrop
            const existingFilenames = this.state.fileList.map(f => f.name);
            const isDuplicate = existingFilenames.includes(newFile.name);
            
            if (isDuplicate) {
                message.info(`${newFile.name} has already been added`);
                return;
            }
            
            // Add file to state with required properties
            this.setState(state => ({
                fileList: [...state.fileList, {
                    uid: Date.now() + Math.random().toString(36).substring(2, 10),
                    status: 'done',
                    name: newFile.name,
                    originFileObj: newFile  // Store the actual file object
                }]
            }), () => {
                console.log("Updated fileList:", this.state.fileList);
            });
        }
    };

    render() {
        const { visible, onManualCreate } = this.props;
        const { fileList, uploading, processingBills } = this.state;

        return (
            <Modal
                visible={visible}
                title="Upload Bills"
                onCancel={this.handleCancel}
                width={600}
                style={{ borderRadius: '8px !important' }}
                footer={null}
                destroyOnClose={true}
                closeIcon={<LucideIcon size='20px' icon={LucideX} />}
            >
                <FlexColumn style={{ width: "100%" }} between gap='24px'>
                    <FlexColumn style={{ height: "100%", justifyContent: "flex-start" }} gap='24px'>
                        <Dragger
                            accept=".pdf"
                            multiple={true}
                            showUploadList={false}
                            customRequest={({ file, onSuccess }) => {
                                // Mark as done so it's available in the onChange handler
                                setTimeout(() => {
                                    onSuccess("ok");
                                }, 0);
                            }}
                            onChange={this.handleDraggerChange}
                            style={{ padding: '20px 0' }}
                        >
                            <Flex start center gap='16px'>
                                <Image src={UploadIcon} width='56px' height='56px' />
                                <FlexColumn start left gap='4px'>
                                    <Text size='16px' weight='500'>Click or drag PDF files to this area to upload</Text>
                                    <Text>Support for single or bulk upload of PDF bills</Text>
                                </FlexColumn>
                            </Flex>
                        </Dragger>
                        
                        {/* File list display */}
                        {
                            fileList.length > 0 && 
                            <Flex start centerH gap='8px'>
                                <LucideFileUp />
                                <Text margin='0' size='16px'>{fileList.length} file{fileList.length > 1 ? 's' : ''} ready to upload</Text>
                            </Flex>
                        }
                        {fileList.length > 0 && (
                            <div className="file-list" style={{ marginBottom: '0px' }}>
                                {fileList.map((file) => (
                                    <div key={file.uid} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '2px 0',
                                        borderBottom: `1px solid ${this.props.theme.colors.divider}`
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            maxWidth: 'calc(100% - 30px)', // Reserve space for the trash icon
                                            overflow: 'hidden'
                                        }}>
                                            <FileTextOutlined style={{ 
                                                marginRight: '8px',
                                                flexShrink: 0 // Prevent icon from shrinking
                                            }} />
                                            <span style={{ 
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '14px'
                                            }} title={file.name}>
                                                {file.name}
                                            </span>
                                        </div>
                                        <LucideIcon 
                                            size='16px' 
                                            onClick={() => this.handleFileRemove(file)} 
                                            icon={LucideTrash2} 
                                            style={{ flexShrink: 0 }} // Prevent trash icon from shrinking
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Manual creation link */}
                        <Flex centerH start gap='4px'>
                            <Text>Don't have a bill PDF?</Text>
                            <a onClick={onManualCreate}>Create one manually</a>
                        </Flex>
                    </FlexColumn>
                    <Flex centerH start gap='16px'>
                        <Button
                            key="submit"
                            solid
                            text="UPLOAD"
                            loading={uploading || processingBills}
                            onClick={this.handleUpload}
                            disabled={fileList.length === 0}
                        />
                        <Button 
                            key="back" 
                            onClick={this.handleCancel}
                            text="CANCEL"
                        />
                    </Flex>
                    
                </FlexColumn>                
            </Modal>
        );
    }
}

function mapStateToProps(state) {
    return {
        store: state.aionAppReducer,
        bankingStore: state.bankingAppReducer
    }
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTheme(BillUpload)));